/**
 * LINK DO PROCESSO NA BLL E NA BNC, colado pelo operador (06/10/2026).
 *
 * A recusa do scrape da BLL mandava "cole o link do bllcompras.com no item", e a tela não tinha onde colar:
 * a rota não aceitava o parâmetro. No PE 59/2026 de Porto Vitória (proposta encerrando no dia seguinte) o
 * operador foi destravado com o link gravado no banco à mão. O backend (#364) passou a aceitar `linkUrl` em
 * `bll-scrape` e `bnc-scrape`: confere a página, busca o token e SÓ ENTÃO grava. As recusas vêm como
 * `{ codigo, mensagem }`.
 *
 * Aqui mora a decisão do front: o QUE fazer vem do `codigo`, nunca de casar a frase (o PCP casa
 * `/ttCD_CHAVE/` na mensagem, e reescrever a mensagem mata o caminho em silêncio). O TEXTO da recusa é o do
 * backend, exibido como vem: ele nasceu da medição dos dois links e é coberto por asserção lá.
 */

export type PortalDoLink = 'bll' | 'bnc';

const PORTAIS: Record<PortalDoLink, { nome: string; host: string }> = {
  bll: { nome: 'BLL', host: 'bllcompras.com' },
  bnc: { nome: 'BNC', host: 'bnccompras.com' },
};

/**
 * Os códigos que levam ao pedido do link.
 * - LINK_NAO_RESOLVIDO: o item não tem link e o PNCP não resolveu. É a PRIMEIRA vez que se pede.
 *   Backend #365 (`recusaLinkNaoResolvido` em link-portal.util.ts). Antes dele esse caso saía em texto puro,
 *   sem código, e o pedido não abria.
 * - LINK_AUSENTE: o que foi colado está vazio, não é URL ou é de outro domínio.
 * - LINK_PAGINA_ERRADA: é do domínio, mas não é a página do processo (ex.: a da tela de proposta).
 * - LINK_SEM_TOKEN: é a página do processo, mas não entregou o token. Nada foi gravado.
 */
export const CODIGOS_QUE_PEDEM_LINK = ['LINK_NAO_RESOLVIDO', 'LINK_AUSENTE', 'LINK_PAGINA_ERRADA', 'LINK_SEM_TOKEN'] as const;

export interface RecusaDeLink {
  codigo: string;
  mensagem: string;
}

/** A recusa de link que veio do backend, ou null se o erro é de outra natureza (e segue como erro). */
export function recusaDeLink(e: any): RecusaDeLink | null {
  const corpo = e?.response?.data;
  const codigo = corpo?.codigo;
  if (typeof codigo !== 'string' || !(CODIGOS_QUE_PEDEM_LINK as readonly string[]).includes(codigo)) return null;
  return { codigo, mensagem: String(corpo?.mensagem ?? '') };
}

/**
 * O corpo do POST do scrape. SEM link, corpo vazio: a chamada sem `linkUrl` continua existindo, e é ela que
 * os itens que já têm link fazem. Mandar `linkUrl` sempre faria esses itens pedirem cola à toa.
 */
export function corpoDoScrape(linkUrl?: string): { linkUrl?: string } {
  const link = String(linkUrl ?? '').trim();
  return link ? { linkUrl: link } : {};
}

/** O pedido do link: diz QUAL página ANTES de o operador colar, e traz a recusa do backend como veio. */
export function pedidoDeLink(portal: PortalDoLink, recusa: RecusaDeLink) {
  const { nome, host } = PORTAIS[portal];
  return {
    title: `Link do processo na ${nome}`,
    instrucao:
      `Abra o processo no ${host} pelo menu Processos e copie a URL dessa página (ela contém /Process/ProcessView). ` +
      'A URL da tela de proposta não serve, mesmo sendo do mesmo site.',
    recusa: recusa.mensagem,
    label: `URL do processo no ${host}`,
    placeholder: `https://${host}/Process/ProcessView?param1=…`,
    confirmLabel: 'Usar este link',
  };
}

export type PedidoDeLink = ReturnType<typeof pedidoDeLink>;

/** Sinal de que o operador desistiu do pedido: não é erro, a tela só não faz nada. */
export const CANCELADO = Symbol('link do portal cancelado');

/**
 * Roda `executar` (sem link). Se o backend recusar por falta ou erro do link, pede o link e chama `comLink`;
 * enquanto a recusa for de link, pede de novo, com a mensagem nova do backend. Cancelar devolve CANCELADO.
 * Erro que não é de link segue como erro, sem pedido.
 */
export async function comLinkDoPortal<T>(opts: {
  portal: PortalDoLink;
  executar: () => Promise<T>;
  comLink: (linkUrl: string) => Promise<T>;
  pedir: (pedido: PedidoDeLink) => Promise<string | null>;
}): Promise<T | typeof CANCELADO> {
  try {
    return await opts.executar();
  } catch (e) {
    let recusa = recusaDeLink(e);
    if (!recusa) throw e;
    for (;;) {
      const link = await opts.pedir(pedidoDeLink(opts.portal, recusa));
      if (link === null) return CANCELADO;
      try {
        return await opts.comLink(link);
      } catch (e2) {
        recusa = recusaDeLink(e2);
        if (!recusa) throw e2;
      }
    }
  }
}
